import type {
  BatchFormResponse,
  BatchPreviewResponse,
  CreatePipelineResponse,
  PipelineRunDetail,
  PipelineRunListResponse,
  PipelineStatePath,
} from "@/lib/batch-processing-api"
import { baseApi } from "./base-api"

export const batchProcessingApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getBatchPipelineRuns: builder.query<PipelineRunListResponse, number>({
      query: (limit) => ({
        url: "/api/batch_processing/runs",
        params: { limit },
      }),
      providesTags: ["PipelineRunList"],
    }),
    getBatchPipelineRun: builder.query<PipelineRunDetail, number>({
      query: (id) => ({ url: `/api/batch_processing/runs/${id}` }),
      transformResponse: (response: { run: PipelineRunDetail }) => response.run,
      providesTags: (_result, _error, id) => [{ type: "PipelineRun", id }],
    }),
    getBatchPipelineStates: builder.query<PipelineStatePath[], number>({
      query: (id) => ({ url: `/api/batch_processing/runs/${id}/states` }),
      transformResponse: (response: { paths: PipelineStatePath[] }) =>
        response.paths,
      providesTags: (_result, _error, id) => [{ type: "PipelineRun", id }],
    }),
    getBatchUploadLimits: builder.query<Record<string, string | null>, void>({
      query: () => ({ url: "/api/batch_processing/upload_limits" }),
      transformResponse: (response: {
        providers: Record<string, string | null>
      }) => response.providers,
    }),
    getBatchForm: builder.query<BatchFormResponse, string | void>({
      query: (jobClassName) => ({
        url: "/api/batch_processing/form",
        params: jobClassName ? { job_class_name: jobClassName } : undefined,
      }),
    }),
    previewBatchPipeline: builder.mutation<
      BatchPreviewResponse,
      {
        jobClassName: string
        jobParams: Record<string, string | number | boolean>
      }
    >({
      query: ({ jobClassName, jobParams }) => ({
        url: "/api/batch_processing/preview",
        method: "POST",
        data: { job_class_name: jobClassName, job_params: jobParams },
      }),
    }),
    createBatchPipeline: builder.mutation<
      CreatePipelineResponse,
      {
        jobClassName: string
        jobParams: Record<string, string | number | boolean>
        launchToken: string
        confirmationName?: string
      }
    >({
      query: ({ jobClassName, jobParams, launchToken, confirmationName }) => ({
        url: "/api/batch_processing/runs",
        method: "POST",
        data: {
          job_class_name: jobClassName,
          job_params: jobParams,
          confirmed: true,
          launch_token: launchToken,
          confirmation_name: confirmationName,
        },
      }),
      invalidatesTags: ["PipelineRunList"],
    }),
    continueBatchPipeline: builder.mutation<void, number>({
      query: (id) => ({
        url: `/api/batch_processing/runs/${id}/continue`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: "PipelineRun", id },
        "PipelineRunList",
      ],
    }),
  }),
})

export const {
  useGetBatchPipelineRunsQuery,
  useGetBatchPipelineRunQuery,
  useGetBatchPipelineStatesQuery,
  useGetBatchUploadLimitsQuery,
  useGetBatchFormQuery,
  useLazyGetBatchFormQuery,
  usePreviewBatchPipelineMutation,
  useCreateBatchPipelineMutation,
  useContinueBatchPipelineMutation,
} = batchProcessingApi
